
import { useState } from 'react';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface MentorProfile {
  id: string;
  user_id: string;
  position: string;
  company: string;
  expertise: string[];
  experience_years: number;
  bio: string;
  calendly_url?: string;
  status: 'pending' | 'approved' | 'rejected';
}

export const useMentorProfile = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [mentorProfile, setMentorProfile] = useState<MentorProfile | null>(null);

  const fetchMentorProfile = async () => {
    if (!user) return null;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('mentors')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        if (error.code !== 'PGRST116') { // PGRST116 is the error for no rows returned
          console.error("Error fetching mentor profile:", error);
        }
        return null;
      }
      
      setMentorProfile(data as MentorProfile);
      return data as MentorProfile;
    } catch (error) {
      console.error("Error fetching mentor profile:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateMentorProfile = async (updates: Partial<MentorProfile>) => {
    if (!user || !mentorProfile) return false;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('mentors')
        .update(updates)
        .eq('user_id', user.id);
      
      if (error) {
        console.error("Error updating mentor profile:", error);
        return false;
      }
      
      setMentorProfile({
        ...mentorProfile,
        ...updates
      });
      
      toast({
        title: "Profile Updated",
        description: "Your mentor profile has been updated successfully"
      });
      
      return true;
    } catch (error) {
      console.error("Error updating mentor profile:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    mentorProfile,
    loading,
    fetchMentorProfile,
    updateMentorProfile
  };
};
